'use client';

import { useEffect, useRef } from 'react';
import { isWindowDefined } from '@utils/validations';

/**
 * Hook that watches for a window property to be assigned and calls a callback when it happens.
 * Polls every 100ms for up to 10 seconds.
 *
 * @example
 * ```tsx
 * useWindowProperty('anonymousId', (id) => {
 *   console.log('Anonymous ID assigned:', id);
 *   // Do something with the ID
 * });
 * ```
 */
export default function useWindowProperty<T = unknown>(propertyName: string, onAssign: (value: T) => void): void {
  const hasCalledRef = useRef(false);

  useEffect(() => {
    if (!isWindowDefined || hasCalledRef.current) {
      return;
    }

    const windowObj = window as unknown as Record<string, unknown>;

    // Check if property is already available
    if (windowObj[propertyName] !== undefined && windowObj[propertyName] !== null) {
      hasCalledRef.current = true;
      onAssign(windowObj[propertyName] as T);
      return;
    }

    // Poll for property to become available
    const intervalId = setInterval(() => {
      if (windowObj[propertyName] !== undefined && windowObj[propertyName] !== null && !hasCalledRef.current) {
        hasCalledRef.current = true;
        clearInterval(intervalId);
        onAssign(windowObj[propertyName] as T);
      }
    }, 100);

    // Fallback: call callback after 10 seconds even if property wasn't found
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      if (!hasCalledRef.current) {
        hasCalledRef.current = true;
        // Call with current value (might be undefined) or pass undefined
        onAssign(windowObj[propertyName] as T);
      }
    }, 10000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [propertyName, onAssign]);
}
